import { useI18n } from 'vue-i18n';
import { Locale, twoLetterLanguageCodes, EnGB } from '../../constants/locales';
import { computed, ComputedRef, Ref } from 'vue';
import { enGB, it, da, de, es, fr, cs, nl, fi, nn, sv, el, ja, pl, pt, zhCN, hu, lv, lt, et, sl, hr, sk, ro, bg, ru, uk } from 'date-fns/locale';

const dateFnLlocaleMap = {
  'en-GB': enGB,
  'it-IT': it,
  'da-DK': da,
  'de-DE': de,
  'es-ES': es,
  'fr-FR': fr,
  'cs-CZ': cs,
  'nl-NL': nl,
  'fi-FI': fi,
  'nn-NO': nn,
  'sv-SE': sv,
  'el-GR': el,
  'ja-JP': ja,
  'pl-PL': pl,
  'pt-PT': pt,
  'zh-CN': zhCN,
  'hu-HU': hu,
  'lv-LV': lv,
  'lt-LT': lt,
  'et-EE': et,
  'sl-SI': sl,
  'hr-HR': hr,
  'sk-SK': sk,
  'ro-RO': ro,
  'bg-BG': bg,
  'ru-RU': ru,
  'uk-UA': uk,
};

export const updateDocumentLanguage = (language: Locale) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('lang', language);
};
export interface IUseTranslationService {
  t: (s: string, q?: number | string | Record<string, number | string>) => string;
  currentLanguage: Ref<Locale, Locale>;
  addMessages: (locale: Locale, messages: Record<string, string>) => void;
  setCurrentLanguage: (language: Locale) => void;
  twoLettersLocaleCode: string;
  dateFnsLocale: ComputedRef<(typeof dateFnLlocaleMap)[keyof typeof dateFnLlocaleMap]>;
}

export const useTranslationService = (): IUseTranslationService => {
  const { t, locale, setLocaleMessage } = useI18n({
    useScope: 'global',
  });
  const twoLettersLocaleCode = computed(() => twoLetterLanguageCodes[(locale.value as Locale) ?? EnGB]);

  const dateFnsLocale = computed(() => {
    return dateFnLlocaleMap?.[locale.value as keyof typeof dateFnLlocaleMap] ?? enGB; // Default to 'enGB'
  });

  const setCurrentLanguage = (language: Locale) => {
    locale.value = language;
    updateDocumentLanguage(language);
    import(`../../Translations/${language}.json`).then(messages => {
      setLocaleMessage(language, messages.default);
    });
  };

  const addMessages = (msgLocale: Locale, messages: Record<string, string>) => {
    setLocaleMessage(msgLocale, messages);
  };
  return {
    currentLanguage: locale as Ref<Locale>,
    setCurrentLanguage,
    t,
    addMessages,
    twoLettersLocaleCode: twoLettersLocaleCode.value,
    dateFnsLocale,
  };
};
